import type { Dispatch } from "react";
import { FormControlLabel, Switch } from "@mui/material";

import { EditableLabel } from "@/features/widget-forms/forms/weather-model-map/components/editable-label";
import DraggableCard, {
  configItemCardStyles,
} from "@/features/widget-forms/forms/weather-model-map/components/draggable-card/draggable-card";
import { fmtSubtitle } from "@/features/widget-forms/forms/weather-model-map/modals/map-layer-formatters";
import type {
  ObservationsMapParameterMapLayerConfiguration,
  ParametersObservedMapLayer,
} from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

import styles from "./observations-layer-card.module.css";

interface ObservationsLayerCardProps {
  config: ObservationsMapParameterMapLayerConfiguration;
  layerInfo?: ParametersObservedMapLayer;
  onRename: Dispatch<string>;
  onEnabledByDefaultChange: Dispatch<boolean>;
  onRemove: () => void;
}

function ObservationsLayerCard({
  config,
  layerInfo,
  onRename,
  onEnabledByDefaultChange,
  onRemove,
}: ObservationsLayerCardProps) {
  const { t } = useTranslation();
  const zoomLevels = layerInfo ? fmtSubtitle(layerInfo.zoom_levels) : "";
  const originalTitle = layerInfo?.label;
  const subtitle = [originalTitle, zoomLevels].filter(Boolean).join(" · ");

  return (
    <DraggableCard
      onRemove={onRemove}
      removeLabel={t("admin.widgets.fields.model_map.general.remove_item", {
        item: config.label,
      })}
    >
      <div className={configItemCardStyles.titleGroup}>
        <div className={configItemCardStyles.title}>
          <EditableLabel
            value={config.label}
            onValueChange={onRename}
            placeholder={originalTitle}
          />
        </div>
        {subtitle ? <div className={configItemCardStyles.subtitle}>{subtitle}</div> : null}
        <div className={styles.defaultVisibleRow}>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled_by_default}
                onChange={(_, checked) => onEnabledByDefaultChange(checked)}
                color="primary"
              />
            }
            label={t("admin.widgets.fields.map.layer.enabledByDefault")}
          />
        </div>
      </div>
    </DraggableCard>
  );
}

export default ObservationsLayerCard;
